import {useState, useEffect, useRef, useCallback, RefObject} from 'react'

const getChildHeights = (el: HTMLElement): number =>
  Array.from(el.children).reduce((sum: number, child): number => sum + child.clientHeight, 0)

type HookResult = {
  navigationHeight: number
  isNavOpen: boolean
  isNavOpening: boolean
  isNavClosing: boolean
  navigationRef: RefObject<HTMLElement>
  toggleNavigation: () => void
}

const useMobileNavState = (transitionPeriod: number): HookResult => {
  const navigationRef = useRef<HTMLElement>(null)
  const [navigationHeight, setNavigationHeight] = useState(0)
  const [isNavOpen, setIsNavOpen] = useState(false)
  const [isNavOpening, setIsNavOpening] = useState(false)
  const [isNavClosing, setIsNavClosing] = useState(false)

  const toggleNavigation = useCallback(() => {
    if (isNavOpen) {
      if (navigationRef.current) {
        setNavigationHeight(getChildHeights(navigationRef?.current))
      }
      setIsNavOpen(false)
    } else {
      // get the mobile nav initial height
      if (navigationRef.current) {
        setNavigationHeight(getChildHeights(navigationRef?.current))
      }
      setIsNavOpening(true)
    }
  }, [isNavOpen])

  useEffect(() => {
    if (isNavOpen === false) {
      setIsNavClosing(true)
      const timeout = setTimeout(() => {
        setIsNavClosing(false)
      }, transitionPeriod)

      return () => {
        clearTimeout(timeout)
      }
    }
    return undefined
  }, [isNavOpen, transitionPeriod])

  useEffect(() => {
    if (isNavOpening) {
      const timeout = setTimeout(() => {
        setIsNavOpen(true)
        setIsNavOpening(false)
      }, transitionPeriod)
      return () => {
        clearTimeout(timeout)
      }
    }
    return undefined
  }, [isNavOpening, transitionPeriod])

  return {
    navigationHeight,
    isNavOpen,
    isNavOpening,
    isNavClosing,
    navigationRef,
    toggleNavigation,
  }
}

export default useMobileNavState
